// Documentation for this service can be found at 'architecture/auth-service-explained.md'
import UnAuthorized from "component/UnAuthorized";
import {
  IBody,
  IResetPassword,
  IResetPasswordByOTP,
  ISignUpService,
  IVerifyOTP,
} from "interface/service";
import LoginUserRequest from "model/api/LoginUserRequest.model";
import React from "react";
import { createRoot } from "react-dom/client";
import { getLocalStorage, getStringInBraces, setLocalStorage } from "util/CommonUtil";
import { LOCAL_STORAGE, MIME_TYPE } from "util/Constant";
import BaseApiService from "./BaseApi.service";
import RequestPasswordChangeRequest from "model/api/RequestPasswordChangeRequest.model";
import ChangeUserPasswordRequest from "model/api/ChangeUserPasswordRequest.model";
import store from "../store";
import { setIsSessionExpired } from "slice/AuthSlice";
const DEFAULT_PATH = "/auth/pub";
const DEFAULT_PATH_SIGNUP = "/signup";
const DEFAULT_PATH_FORGOT_PASSWORD = "/password";
const DEFAULT_USER_PATH = "/v1/user";

class AuthService extends BaseApiService {
  private tokenRenewalPromise: Promise<any> | null = null;
  private pendingRequests: any[] = [];

  constructor() {
    super();

    const cancelTokenMap = new Map();

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this;

    const axios = this.getAxios();

    this.getAxios().interceptors.request.use(
      async function (config) {
        console.log("Making API Request...");
        // Do something before request is sent

        //Handle cancel-token request from the page level
        if (config.url?.includes("cancel-token")) {
          const token = getStringInBraces(config.url ?? "");
          const cancelToken = cancelTokenMap.get(token);
          if (cancelToken) {
            cancelToken.cancel?.({
              status: 499,
              message: "Request cancelled as a new request is created",
              config: config,
            });
          }

          //Stop the original request
          return Promise.reject({
            status: 499,
            message: "Request cancelled as a new request is created",
          });
        }

        //If same API call is going on, cancel the previous one
        if (!config.url?.includes("/user/renew")) {
          let name = `${config.method}::${config.url}`;

          const isSessionListAPI = config.url?.includes("/session/list");

          if (isSessionListAPI) {
            const payloadString = JSON.stringify(config.data);

            //Make a hash of the payload
            const encoder = new TextEncoder();
            const data = encoder.encode(payloadString);
            const hashBuffer = await crypto.subtle.digest("SHA-256", data);
            const payloadHash = Array.from(new Uint8Array(hashBuffer))
              .map((b) => b.toString(16).padStart(2, "0"))
              .join("");

            name = `${name}::${payloadHash}`;
          }

          const isSameAPICallRunning = cancelTokenMap.get(name);

          if (isSameAPICallRunning) {
            isSameAPICallRunning.cancel?.({
              status: 499,
              message: "Request cancelled as a new request is created",
              config: config,
            });
          }

          const cancelToken = axios.CancelToken?.source();

          cancelTokenMap.set(name, cancelToken);

          if (cancelToken) config.cancelToken = cancelToken.token;
        }

        return config;
      },
      function (error) {
        console.log("API Request ERROR...");
        // Do something with request error
        return Promise.reject(error);
      }
    );

    console.log("Creating interceptor...");
    // Add a response interceptor
    this.getAxios().interceptors.response.use(
      (response) => {
        console.log("API Response received...");
        return response;
      },
      this.handleResponseError(axios, cancelTokenMap)
    );
  }

  //Get the front end info
  private getFrontEndInfo() {
    return {
      browserInfo: {
        userAgent: navigator.userAgent,
      },
      stackTrace: null,
      resolution: { width: window.screen.width, height: window.screen.height },
      screenInfo: {
        width: window.screen.width,
        height: window.screen.height,
        availWidth: window.screen.availWidth,
        availHeight: window.screen.availHeight,
        colorDepth: window.screen.colorDepth,
        pixelDepth: window.screen.pixelDepth,
      },
      location: {
        hostname: window.location.hostname,
        href: window.location.href,
        origin: window.location.origin,
        pathname: window.location.pathname,
        port: window.location.port,
        protocol: window.location.protocol,
        search: window.location.search,
      },
    };
  }

  private handleTokenRenewal(originalReq: any) {
    // If there's already a token renewal in progress, add this request to the queue
    if (this.tokenRenewalPromise) {
      return new Promise((resolve, reject) => {
        this.pendingRequests.push({ originalReq, resolve, reject });
      });
    }

    // Start a new token renewal process
    this.tokenRenewalPromise = new Promise((resolve, reject) => {
      console.log("Trying renew auth...");
      const userInfo = JSON.parse(getLocalStorage(LOCAL_STORAGE.USER_INFO));
      const currentUser = JSON.parse(getLocalStorage(LOCAL_STORAGE.CURRENT_USER));
      const refreshToken = userInfo.refreshToken;
      const url = this.getDefaultApiUrl();
      const finalUrl = `${url}/v1/user/renew`;

      fetch(finalUrl, {
        method: "post",
        headers: {
          "Content-Type": "application/json",
          "X-Client-Identifier": "web-app",
        },
        body: JSON.stringify({
          email: userInfo?.email || currentUser?.email,
          refresh_token: refreshToken,
        }),
      })
        .then((response) => response.json())
        .then((response) => {
          this.updateUserInfo(response);

          // Process the original request
          originalReq.headers = {
            ...originalReq.headers,
            Authorization: "Bearer " + response.access_token,
          };

          // Process all pending requests with the new token
          while (this.pendingRequests.length > 0) {
            const { originalReq: req, resolve: res, reject: rej } = this.pendingRequests.shift();
            req.headers = {
              ...req.headers,
              Authorization: "Bearer " + response.access_token,
            };
            this.getAxios()(req).then(res).catch(rej);
          }

          return this.getAxios()(originalReq);
        })
        .then((response) => {
          this.tokenRenewalPromise = null;
          resolve(response);
        })
        .catch((err) => {
          this.tokenRenewalPromise = null;
          this.pendingRequests = []; // Clear pending requests on error
          console.log(err);
          reject(err);
        });
    });

    return this.tokenRenewalPromise;
  }

  private updateUserInfo(response: any) {
    const parsedUserInfo = JSON.parse(getLocalStorage(LOCAL_STORAGE.USER_INFO) ?? "{}");
    parsedUserInfo.accessToken = response.access_token;
    parsedUserInfo.refreshToken = response.refresh_token;
    parsedUserInfo.expiresIn = response.expires_in;
    parsedUserInfo.loggedInTime = new Date().getTime();
    if (response.access_token) {
      setLocalStorage(LOCAL_STORAGE.USER_INFO, JSON.stringify(parsedUserInfo));
    }
  }

  //Send bug report to the server
  private sendBugReport(error: any, frontEndInfo: any) {
    try {
      const accessToken = JSON.parse(getLocalStorage(LOCAL_STORAGE.USER_INFO) || "{}")?.accessToken;
      if (!accessToken) return;

      const url = this.getDefaultApiUrl();
      const finalUrl = `${url}/v1/bug-report`;
      //Remove the token from the error object
      if (error.config?.headers) {
        delete error.config.headers.Authorization;
      }

      fetch(finalUrl, {
        method: "post",
        headers: {
          "Content-Type": "application/json",
          "X-Client-Identifier": "web-app",
          Authorization: "Bearer " + accessToken,
        },
        body: JSON.stringify({
          data: JSON.stringify({ ...error, frontEndInfo }),
        }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to send bug report: ${response.statusText}`);
          }
          return response.json();
        })
        .then((data) => {
          console.log("Bug report sent successfully:", data);
        })
        .catch((fetchError) => {
          console.error("Error in sending bug report:", fetchError);
        });
    } catch (e) {
      console.log("Error in sending error report", e);
    }
  }

  private handleResponseError = (axios: any, cancelTokenMap: Map<string, any>) => (error: any) => {
    // const frontEndInfo = this.getFrontEndInfo();
    // frontEndInfo.stackTrace = error?.stack ?? null;

    const originalReq = error.config;

    if (typeof window === "undefined") return Promise.reject(error);

    console.log("API Response ERROR...");

    const isLoginRelatedRequest =
      originalReq?.url?.includes?.("/login") ||
      originalReq?.url?.includes?.("/auth_code_exchange") ||
      (originalReq?.url?.endsWith("/user") && originalReq?.method === "post");

    // Handle token expiration
    if (error?.response?.status === 401 && originalReq && !originalReq.___retry) {
      originalReq.___retry = true;
      console.log("Token Expired...");

      if (!isLoginRelatedRequest) {
        return this.handleTokenRenewal(originalReq);
      }
    }

    // Handle failed refresh attempt
    if (error?.config?.___retry && error?.response?.status === 401) {
      const isTokenRefreshRequest = originalReq.url?.includes?.("/user/renew");

      // Only show unauthorized error if it's a failed token refresh
      if (!isTokenRefreshRequest && !isLoginRelatedRequest) {
        // Dispatch session expired action to stop polling
        store.dispatch(setIsSessionExpired(true));

        const container = document.getElementById("errorDiv12399");
        const errorContainerRoot = createRoot(container!);
        errorContainerRoot.render(React.createElement(UnAuthorized));
      }
    }

    // Handle cancelled requests
    if (axios.isCancel(error) || error?.status === 499) {
      const name = `${originalReq?.method}::${originalReq?.url}`;
      cancelTokenMap.delete(name);
      return Promise.reject(error?.message);
    }

    // Send bug report
    // this.sendBugReport(error, frontEndInfo);

    return Promise.reject(error);
  };

  login(payload: LoginUserRequest) {
    const url = `/v1/user/login`;
    return this.makePostRequest(url, payload);
  }

  loginWithAuth0Code(code: string) {
    const url = `/v2/auth_code_exchange/${code}`;
    return this.makeGetRequest(url);
  }

  logOut() {
    // Keys you want to preserve
    const keysToPreserve = ["defaultLocale", "theme", "BASE_API_URL_OVERRIDDEN"];

    // Iterate through localStorage keys in reverse order
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && !keysToPreserve.includes(key)) {
        // Remove the key-value pair from localStorage
        localStorage.removeItem(key);
      }
    }

    localStorage.removeItem(LOCAL_STORAGE.USER_INFO);
  }

  signUp(payload: ISignUpService) {
    const endPoint = DEFAULT_PATH_SIGNUP;
    return this.makePostRequest(endPoint, payload);
  }
  resendSMS(email: string) {
    const endPoint = DEFAULT_PATH_SIGNUP;
    const headers = {
      "content-type": MIME_TYPE.PLAIN,
    };
    return this.axiosConfig(endPoint, "put", headers, email);
  }
  verifyToken(token: string) {
    const endPoint = `${DEFAULT_PATH_SIGNUP}/email/verify/${token}`;
    return this.makePostRequest(endPoint);
  }

  generateToken(code: string) {
    const baseUrl = DEFAULT_PATH;
    const url = `${baseUrl}/google/token?&code=${code}`;
    return this.makeGetRequest(url);
  }

  sendResetPasswordLink(email: string) {
    const endPoint = `${DEFAULT_PATH_FORGOT_PASSWORD}/reset`;
    const headers = {
      "content-type": MIME_TYPE.PLAIN,
    };
    return this.axiosConfig(endPoint, "post", headers, email);
  }
  resetPassword(object: IResetPassword) {
    const endPoint = `${DEFAULT_PATH_FORGOT_PASSWORD}/reset`;
    return this.makePutRequest(endPoint, object);
  }

  resetPasswordByOTP(object: IResetPasswordByOTP) {
    const endPoint = `${DEFAULT_PATH}/otp/send`;
    return this.makePostRequest(endPoint, object);
  }

  verifyOTP(object: IVerifyOTP, data: IBody) {
    const endPoint = `${DEFAULT_PATH}/reset/password`;
    const headers = {
      Authorization: `Basic ${btoa(data.username + ":" + data.password)}`,
    };
    return this.makePostRequest(endPoint, object, headers);
  }

  requestResetPasswordLink(payload: RequestPasswordChangeRequest) {
    const url = `${DEFAULT_USER_PATH}/request_password_change`;
    return this.makePostRequest(url, payload);
  }

  changeUserPassword(payload: ChangeUserPasswordRequest) {
    const url = `${DEFAULT_USER_PATH}/change_password`;
    return this.makePostRequest(url, payload);
  }
}

export default new AuthService();
